import React from 'react';
import { useSubscription } from '../SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { auth } from '../firebase';

const UnpaidStatusBanner = () => {
    const { accountStatus, pastDueAt } = useSubscription();
    const { t } = useTranslation();
    const location = useLocation();

    // 既に支払い設定画面にいる場合はバナーを出さない（または重複を避ける）
    const isPaymentPage = location.pathname === '/settings/customer-info';
    if (accountStatus !== 'past_due' || isPaymentPage) return null;

    // 計算: pastDueAt + 14日
    let limitDateStr = '';
    if (pastDueAt) {
        const date = new Date(pastDueAt);
        date.setDate(date.getDate() + 14);
        limitDateStr = `${date.getMonth() + 1}月${date.getDate()}日`;
    }

    return (
        <div className="bg-red-600 text-white py-3 px-4 text-center sticky top-0 z-50">
            <p className="font-bold">
                {t('billing.past_due_warning_with_date', { 
                    defaultValue: `お支払いに失敗しています。${limitDateStr}までにお支払い情報を更新してください。`,
                    date: limitDateStr 
                })}
                <Link to="/settings/customer-info" className="ml-4 underline hover:text-red-100">
                    {t('billing.update_payment_info', 'お支払い情報を更新する')}
                </Link>
            </p>
        </div>
    );
};

const SuspendedOverlay = () => {
    const { accountStatus } = useSubscription();
    const { t } = useTranslation();
    const location = useLocation();

    // 許可されたパスの判定
    const isAllowedPath = 
        location.pathname === '/settings/billwave' ||
        location.pathname === '/settings/customer-info' ||
        location.pathname === '/settings/billing-history' ||
        location.pathname.startsWith('/app/support/'); // 使い方、CSVフォーマット、FAQ、トラブル、お問い合わせ等

    if (accountStatus !== 'suspended' || isAllowedPath) return null;

    const handleLogout = () => {
        auth.signOut();
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-95 z-[100] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center border-t-8 border-red-500">
                <div className="mb-6">
                    <svg className="w-20 h-20 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                    {t('billing.suspended_title', 'サービスは停止されています')}
                </h2>
                <p className="text-gray-600 mb-8">
                    {t('billing.suspended_message', '未払い料金のお支払い後、自動的に利用が再開されます。')}
                </p>
                <div className="space-y-4">
                    <Link to="/settings/customer-info" className="block w-full bg-blue-600 text-white font-bold py-3 px-4 rounded hover:bg-blue-700 transition duration-200">
                        {t('billing.update_payment_info', '支払い情報を更新')}
                    </Link>
                    <Link to="/settings/billing-history" className="block w-full bg-gray-100 text-gray-700 font-bold py-3 px-4 rounded hover:bg-gray-200 transition duration-200">
                        {t('billing.view_history', '請求履歴を見る')}
                    </Link>
                    <button onClick={handleLogout} className="block w-full text-gray-500 font-medium py-2 hover:text-gray-700 underline">
                        {t('common.logout', 'ログアウト')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export { UnpaidStatusBanner, SuspendedOverlay };
